import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import DateWidget from '~/components/Common/Elements/DateWidget';
import { getAvailabilityForDate } from '~/components/Vendors/Properties/helpers/availability';

import type { AvailabilityBlock } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useSetRoomTypeAvailability } from '~/hooks/apis/reservation/useSetRoomTypeAvailability';

import { SYDNEY_TIMEZONE, formatDateISO } from '~/services/TimeService';

function datesBetween(startDate: string, endDate: string): Array<string> {
  const dates = [];
  const end = dayjs.tz(endDate, SYDNEY_TIMEZONE);
  let date = dayjs.tz(startDate, SYDNEY_TIMEZONE);
  while (date.isBefore(end, 'day') || date.isSame(end, 'day')) {
    dates.push(date.format('YYYY-MM-DD'));
    date = date.add(1, 'day');
  }
  return dates;
}

interface Props {
  open: boolean;
  propertyId: string;
  roomTypeId: string;
  roomTypeName: string;
  allocation: number;
  availability: Array<AvailabilityBlock>;
  defaultStartDate?: string;
  defaultEndDate?: string;
  onClose: () => void;
  onApplied: () => void;
}

export default function SetSalesforceAllocationModal({
  open,
  propertyId,
  roomTypeId,
  roomTypeName,
  allocation,
  availability,
  defaultStartDate,
  defaultEndDate,
  onClose,
  onApplied,
}: Props) {
  const today = formatDateISO(new Date());
  const [startDate, setStartDate] = useState(defaultStartDate ?? today);
  const [endDate, setEndDate] = useState(defaultEndDate ?? today);
  const [roomCount, setRoomCount] = useState(allocation);
  const { enqueueSnackbar } = useSnackbar();
  const setAvailability = useSetRoomTypeAvailability();

  useEffect(() => {
    if (defaultStartDate) {
      setStartDate(defaultStartDate);
    }
    if (defaultEndDate) {
      setEndDate(defaultEndDate);
    }
  }, [defaultStartDate, defaultEndDate]);

  function handleApply() {
    const ranges = datesBetween(startDate, endDate).map((date) => {
      const bookings = getAvailabilityForDate(date, availability)?.count ?? 0;
      return {
        start_date: date,
        end_date: date,
        delta: bookings + roomCount,
      };
    });
    setAvailability.mutate(
      { propertyId, roomTypeId, ranges },
      {
        onSuccess: () => {
          enqueueSnackbar(`Set inventory to ${roomCount}/day for ${roomTypeName}`, {
            variant: 'success',
          });
          onApplied();
          onClose();
        },
        onError: (error: Error) => enqueueSnackbar(`Failed to set inventory: ${error.message}`, { variant: 'error' }),
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set inventory from Salesforce</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sets available rooms/day for {roomTypeName}, in addition to any rooms already booked.
        </Typography>

        <TextField
          type="number"
          label="Available rooms/day"
          value={roomCount}
          onChange={(event) => setRoomCount(event.target.value === '' ? 0 : Number(event.target.value))}
          inputProps={{ min: '0' }}
          fullWidth
        />

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box flex={1}>
              <DateWidget label="Start date" value={startDate} onChange={setStartDate} fullWidth />
            </Box>
            <Box flex={1}>
              <DateWidget label="End date" value={endDate} onChange={setEndDate} fullWidth />
            </Box>
          </Stack>
          <FormHelperText>Defaults to the opportunity&apos;s Salesforce travel dates.</FormHelperText>
        </Box>

        {startDate > endDate && (
          <Alert severity="error" sx={{ mt: 2 }}>
            End date must be on or after the start date.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={setAvailability.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={startDate > endDate || roomCount < 0 || setAvailability.isPending}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
